import type { BlockVolumeAttachment } from '@xver/apis/v1/block-storage/model';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

const LINK_CLASS =
    'min-h-6 inline-flex items-center font-medium text-fg underline-offset-2 hover:underline break-all';

/** "web-1 · 27.254.163.x" or "api · /data", linked to the VM or container page. */
export default function AttachmentSummary({
    attachment,
    onNavigate,
}: {
    attachment: BlockVolumeAttachment | null;
    /** Runs before following the link, e.g. to make the resource's project active. */
    onNavigate?: () => void;
}) {
    const { t } = useTranslation('blockStorage');

    if (!attachment) return <span className="text-sm text-fg/68">{t('notAttached')}</span>;

    if (attachment.type === 'vm') {
        if (!attachment.instanceId) {
            return <span className="text-sm text-fg/78">{t('hiddenVm')}</span>;
        }
        return (
            <div className="flex flex-col items-start gap-0.5 text-sm">
                <span className="sr-only">{t('attach.types.vm')}:</span>
                <Link
                    to={`/cloud-compute/${attachment.instanceId}`}
                    onClick={onNavigate}
                    className={LINK_CLASS}
                >
                    {attachment.hostname}
                </Link>
                <span className="font-mono text-xs text-fg/68">{attachment.ipAddress}</span>
                {attachment.pendingReboot && (
                    <span className="text-xs text-fg/78">{t('pendingRebootShort')}</span>
                )}
            </div>
        );
    }

    if (!attachment.containerId) {
        return <span className="text-sm text-fg/78">{t('hiddenContainer')}</span>;
    }

    return (
        <div className="flex flex-col items-start gap-0.5 text-sm">
            <span className="sr-only">{t('attach.types.container')}:</span>
            <Link
                to={`/container-engine/${attachment.containerId}`}
                onClick={onNavigate}
                className={LINK_CLASS}
            >
                {attachment.containerName}
            </Link>
            <span className="font-mono text-xs text-fg/68 break-all">{attachment.mountPath}</span>
        </div>
    );
}
